import type { AnalyticsBrowser as AnalyticsBrowserInstance } from "@customerio/cdp-analytics-browser";

import { PROGRESS_EVENT_NAME } from "~/lib/analytics.utils";

import type { AnalyticsAdapter } from "../types";

const isBrowser = typeof window !== "undefined";
const CUSTOMERIO_SKIPPED_EVENTS = new Set([PROGRESS_EVENT_NAME]);

let analyticsInstance: AnalyticsBrowserInstance | null = null;
let initialized = false;
let initPromise: Promise<void> | null = null;
let pendingOperations: Array<(analytics: AnalyticsBrowserInstance) => void> = [];

export class CustomerIoAdapter implements AnalyticsAdapter {
  init(): Promise<void> | void {
    if (!isBrowser || initialized) return;
    if (initPromise) return initPromise;

    const writeKey = import.meta.env.VITE_CUSTOMERIO_CDP_WRITE_KEY;
    if (!writeKey) {
      if (import.meta.env.DEV) {
        console.warn("Customer.io analytics provider configured without VITE_CUSTOMERIO_CDP_WRITE_KEY");
      }
      return;
    }

    const cdnURL = import.meta.env.VITE_CUSTOMERIO_CDP_URL;

    initPromise = import("@customerio/cdp-analytics-browser")
      .then(({ AnalyticsBrowser }) => {
        const analytics = AnalyticsBrowser.load(
          {
            writeKey,
            ...(cdnURL ? { cdnURL } : {}),
          },
          { initialPageview: false },
        );
        analyticsInstance = analytics;
        initialized = true;
        const operations = pendingOperations;
        pendingOperations = [];
        for (const operation of operations) {
          operation(analytics);
        }
      })
      .catch((error) => {
        console.error("Failed to initialize Customer.io analytics", error);
        analyticsInstance = null;
        initialized = false;
        initPromise = null;
      });

    return initPromise;
  }

  page(input: { name?: string; properties?: Record<string, unknown> }): void {
    if (!isBrowser) return;
    this.runWhenInitialized((analytics) => {
      void analytics.page(input.name, input.properties);
    });
  }

  track(input: { event: string; properties?: Record<string, unknown> }): void {
    if (!isBrowser) return;
    if (CUSTOMERIO_SKIPPED_EVENTS.has(input.event)) return;
    this.runWhenInitialized((analytics) => {
      void analytics.track(input.event, input.properties);
    });
  }

  identify(input: { userId?: string; traits?: Record<string, unknown> }): void {
    if (!isBrowser || !input.userId) return;
    this.runWhenInitialized((analytics) => {
      void analytics.identify(input.userId, input.traits);
    });
  }

  reset(): void {
    if (!isBrowser) return;
    this.runWhenInitialized((analytics) => {
      analytics.reset();
    });
  }

  private runWhenInitialized(operation: (analytics: AnalyticsBrowserInstance) => void): void {
    if (initialized && analyticsInstance) {
      operation(analyticsInstance);
      return;
    }

    if (!import.meta.env.VITE_CUSTOMERIO_CDP_WRITE_KEY) return;
    pendingOperations.push(operation);
    void this.init();
  }
}
